import type { components } from "@brandex/api-client";
import { formatRelative, type MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, Field, Select, Spinner, Textarea, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, ExternalLink, FileText, History, Image as ImageIcon, Mic, MessageSquareText, PlayCircle, Sparkles } from "lucide-react";
import { useState } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { useMyOrganizations } from "../queries";

export type Deliverable = components["schemas"]["DeliverableDto"];
type Status = Deliverable["status"];
type Event = components["schemas"]["DeliverableEventDto"];
type Publication = components["schemas"]["PublicationDto"];
type Shoot = components["schemas"]["ShootDto"];

/** How often the page checks for new items from the team. */
const REFRESH_MS = 30_000;

/** Filters the doctor uses; "in production" groups the team's working steps. */
type Tab = "all" | "sent_to_doctor" | "in_production" | "changes_requested" | "approved" | "published";
const TABS: readonly Tab[] = ["all", "sent_to_doctor", "in_production", "changes_requested", "approved", "published"];
const IN_PRODUCTION: readonly Status[] = ["planned", "shot", "editing", "internal_review"];

export function inTab(tab: Tab, status: Status): boolean {
  if (tab === "all") return true;
  if (tab === "in_production") return IN_PRODUCTION.includes(status);
  return status === tab;
}

export const STATUS_TONE: Record<Status, "success" | "warning" | "neutral" | "accent"> = {
  planned: "neutral",
  shot: "neutral",
  editing: "neutral",
  internal_review: "neutral",
  sent_to_doctor: "accent",
  changes_requested: "warning",
  approved: "success",
  published: "success",
};

/** The doctor's view of progress: five plain steps instead of eight internal ones. */
const STAGES = ["plan", "production", "review", "approved", "live"] as const;
type Stage = (typeof STAGES)[number];
export function stageOf(status: Status): Stage {
  switch (status) {
    case "planned":
      return "plan";
    case "shot":
    case "editing":
    case "internal_review":
      return "production";
    case "sent_to_doctor":
    case "changes_requested":
      return "review";
    case "approved":
      return "approved";
    case "published":
      return "live";
  }
}

const KIND_ICON: Record<Deliverable["kind"], typeof FileText> = {
  podcast: Mic, reel: PlayCircle, promo_shoot: PlayCircle, photo_card: ImageIcon, article: FileText, website_section: FileText, other: Sparkles,
};

export const contentKey = (orgId: string) => ["orgs", orgId, "deliverables"] as const;

export function useDeliverables(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<Deliverable[]>({
    queryKey: contentKey(orgId ?? ""),
    enabled: orgId !== undefined,
    // New items from the team appear without a reload (the bell also
    // triggers a refresh when something is sent).
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/deliverables", { params: { path: { org_id: orgId ?? "" } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
}

export function useUpcomingShoots(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<Shoot[]>({
    queryKey: ["orgs", orgId ?? "", "shoots"],
    enabled: orgId !== undefined,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/shoots", { params: { path: { org_id: orgId ?? "" } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
}

/** Date and time in Bangladesh, in the doctor's language. */
export function shootWhen(locale: "en" | "bn", iso: string): string {
  return new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
    timeZone: "Asia/Dhaka", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

function usePublications(orgId: string | undefined) {
  const { api } = useBrandex();
  return useQuery<Publication[]>({
    queryKey: ["orgs", orgId ?? "", "publications"],
    enabled: orgId !== undefined,
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/publications", { params: { path: { org_id: orgId ?? "" } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
}

/**
 * Content and approvals (F-09, F-10): everything BrandEx makes for the
 * doctor. Items waiting for them lead the page, large, with the preview,
 * caption and Approve / Request changes. Below, every item with a five-step
 * progress line, filters with counts and a month picker. Nothing is
 * published without their approval. The page refreshes itself.
 */
export function ContentView() {
  const t = useT();
  const { locale } = useBrandex();
  const orgs = useMyOrganizations();
  const orgId = orgs.data?.[0]?.organization.id;
  const items = useDeliverables(orgId);
  const publications = usePublications(orgId);
  const [tab, setTab] = useState<Tab>("all");
  const [month, setMonth] = useState("");
  if (orgs.isPending || (orgId !== undefined && items.isPending)) return <Spinner label={t("common.loading")} />;
  if (orgs.isError || items.isError) return <Alert tone="error">{errorMessage(orgs.error ?? items.error, t)}</Alert>;
  const all = items.data ?? [];
  const months = [...new Set(all.map((d) => d.period))].sort().reverse();
  const inMonth = month === "" ? all : all.filter((d) => d.period === month);
  const waiting = inMonth.filter((d) => d.status === "sent_to_doctor");
  const shown = inMonth.filter((d) => inTab(tab, d.status) && !(tab === "all" && d.status === "sent_to_doctor"));
  const pubs = publications.data ?? [];
  const monthName = (p: string) =>
    new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${p}-01T00:00:00Z`));
  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("content.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("content.subtitle")}</p>
        </div>
        {months.length > 1 ? (
          <Field label={t("content.month")} className="w-full sm:w-52">
            <Select value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">{t("content.allMonths")}</option>
              {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
            </Select>
          </Field>
        ) : null}
      </header>

      {all.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">{t("content.empty")}</CardContent></Card>
      ) : (
        <>
          {waiting.length > 0 ? (
            <section aria-labelledby="waiting-title" className="grid gap-3">
              <div>
                <h2 id="waiting-title" className="flex items-center gap-2 font-display text-xl font-bold">
                  {t("content.waitingTitle")}
                  <span className="rounded-full bg-primary px-2.5 py-0.5 text-sm text-primary-foreground tabular-nums">{waiting.length}</span>
                </h2>
                <p className="text-sm text-muted-foreground">{t("content.waitingHint")}</p>
              </div>
              <ul className="grid gap-4 md:grid-cols-2">
                {waiting.map((d) => <ReviewCard key={d.id} orgId={orgId ?? ""} item={d} />)}
              </ul>
            </section>
          ) : (
            <Alert tone="success">{t("content.allClear")}</Alert>
          )}

          <section aria-labelledby="all-title" className="grid gap-3">
            <h2 id="all-title" className="font-display text-xl font-bold">{t("content.allTitle")}</h2>
            <div role="tablist" aria-label={t("content.filter")} className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {TABS.map((k) => {
                const n = inMonth.filter((d) => inTab(k, d.status)).length;
                const label = t(k === "all" ? "content.tab.all" : k === "in_production" ? "content.tab.in_production" : (`content.status.${k}` as MessageKey));
                return (
                  <button
                    key={k}
                    role="tab"
                    type="button"
                    aria-selected={tab === k}
                    aria-label={`${label}, ${n}`}
                    onClick={() => setTab(k)}
                    className={cn(
                      "inline-flex shrink-0 items-center gap-2 rounded-[var(--radius-pill)] border px-3 py-1.5 text-sm font-medium transition-colors",
                      tab === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted",
                    )}
                  >
                    {label}
                    <span aria-hidden className={cn("rounded-full px-1.5 text-xs tabular-nums", tab === k ? "bg-white/20" : "bg-muted text-muted-foreground")}>{n}</span>
                  </button>
                );
              })}
            </div>
            {shown.length === 0 ? (
              <p className="rounded-[var(--radius-lg)] border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                {tab === "sent_to_doctor" ? t("content.allClear") : t("content.noneInTab")}
              </p>
            ) : (
              <ul className="grid gap-3">
                {shown.map((d) => (
                  <ContentRow key={d.id} orgId={orgId ?? ""} item={d} published={pubs.filter((p) => p.deliverable_id === d.id)} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
      <p className="text-xs text-muted-foreground">{t("content.footer")}</p>
    </div>
  );
}

/** An item waiting for the doctor: preview first, then decide. */
function ReviewCard({ orgId, item }: { orgId: string; item: Deliverable }) {
  const t = useT();
  const { platform, locale } = useBrandex();
  const [showHistory, setShowHistory] = useState(false);
  const Icon = KIND_ICON[item.kind];
  return (
    <li className="glass grid content-start gap-4 rounded-[var(--radius-xl)] p-5 ring-2 ring-primary/40">
      <div className="flex items-start gap-3">
        <span aria-hidden className="grid h-11 w-11 shrink-0 place-items-center rounded-[var(--radius-lg)] bg-primary/15 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="font-display text-lg font-bold leading-snug [overflow-wrap:anywhere]">{item.title}</p>
          <p className="text-xs text-muted-foreground">
            {t(`content.kind.${item.kind}` as MessageKey)} · {t("content.due", { date: item.due_on })}
            {item.revision > 1 ? ` · ${t("content.revision", { n: item.revision })}` : ""}
          </p>
        </div>
      </div>
      {item.preview_url ? (
        <Button variant="outline" className="h-12 w-full justify-center" onClick={() => void platform.openExternal(item.preview_url ?? "")}>
          <ExternalLink className="h-4 w-4" aria-hidden />
          {t("content.preview")}
        </Button>
      ) : null}
      {item.caption ? (
        <p className="rounded-md bg-muted px-3 py-2 text-sm"><span className="font-medium">{t("content.caption")}: </span>{item.caption}</p>
      ) : null}
      <Decision orgId={orgId} item={item} />
      {item.revision > 1 ? (
        <div className="grid gap-2">
          <Button size="sm" variant="ghost" className="w-fit" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
            <History className="h-4 w-4" aria-hidden />
            {t("content.history")}
          </Button>
          {showHistory ? <HistoryList orgId={orgId} id={item.id} locale={locale} /> : null}
        </div>
      ) : null}
    </li>
  );
}

/** Approve, or say what should change (a note is required). */
function Decision({ orgId, item }: { orgId: string; item: Deliverable }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [comment, setComment] = useState("");
  const path = { params: { path: { org_id: orgId, deliverable_id: item.id } } };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: contentKey(orgId) });
    void qc.invalidateQueries({ queryKey: ["orgs", orgId, "deliverables", item.id, "events"] });
  };
  const approve = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/orgs/{org_id}/deliverables/{deliverable_id}/approve", path);
    },
    onSuccess: refresh,
  });
  const requestChanges = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/orgs/{org_id}/deliverables/{deliverable_id}/request-changes", { ...path, body: { comment: comment.trim() } });
    },
    onSuccess: () => {
      setAsking(false);
      setComment("");
      refresh();
    },
  });
  return (
    <div className="grid gap-3">
      {approve.isError ? <Alert tone="error">{errorMessage(approve.error, t)}</Alert> : null}
      {requestChanges.isError ? <Alert tone="error">{errorMessage(requestChanges.error, t)}</Alert> : null}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="accent" loading={approve.isPending} onClick={() => approve.mutate()} aria-label={t("content.approveItem", { title: item.title })}>
          <Check className="h-4 w-4" aria-hidden />
          {t("content.approve")}
        </Button>
        <Button variant="outline" onClick={() => setAsking((v) => !v)} aria-expanded={asking}>
          <MessageSquareText className="h-4 w-4" aria-hidden />
          {t("content.requestChanges")}
        </Button>
      </div>
      {asking ? (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (comment.trim().length >= 3) requestChanges.mutate();
          }}
        >
          <Field label={t("content.whatToChange")} hint={t("content.whatToChangeHint")}>
            <Textarea rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} autoFocus />
          </Field>
          <div><Button type="submit" size="sm" disabled={comment.trim().length < 3} loading={requestChanges.isPending}>{t("content.send")}</Button></div>
        </form>
      ) : null}
    </div>
  );
}

function ContentRow({ orgId, item, published }: { orgId: string; item: Deliverable; published: Publication[] }) {
  const t = useT();
  const { platform, locale } = useBrandex();
  const [showHistory, setShowHistory] = useState(false);
  const Icon = KIND_ICON[item.kind];
  return (
    <li className="glass grid gap-3 rounded-[var(--radius-lg)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span aria-hidden className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="font-medium [overflow-wrap:anywhere]">{item.title}</p>
            <p className="text-xs text-muted-foreground">
              {t(`content.kind.${item.kind}` as MessageKey)} · {t("content.due", { date: item.due_on })}
              {item.revision > 1 ? ` · ${t("content.revision", { n: item.revision })}` : ""}
            </p>
          </div>
        </div>
        <Badge tone={STATUS_TONE[item.status]}>{t(`content.status.${item.status}` as MessageKey)}</Badge>
      </div>
      <Progress status={item.status} />
      {published.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("content.publishedOn")}</span>
          {published.map((p) => (
            <Button key={p.id} size="sm" variant="outline" onClick={() => void platform.openExternal(p.url)}>
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              {channelName(t, p)}
            </Button>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {item.preview_url && item.status !== "planned" ? (
          <Button size="sm" variant="outline" onClick={() => void platform.openExternal(item.preview_url ?? "")}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            {t("content.preview")}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
          <History className="h-4 w-4" aria-hidden />
          {t("content.history")}
        </Button>
      </div>
      {showHistory ? <HistoryList orgId={orgId} id={item.id} locale={locale} /> : null}
    </li>
  );
}

/** Planned → In production → Your review → Approved → Live. */
function Progress({ status }: { status: Status }) {
  const t = useT();
  const at = STAGES.indexOf(stageOf(status));
  return (
    <ol aria-label={t("content.progress")} className="grid grid-cols-5 gap-1">
      {STAGES.map((s, i) => {
        const done = i < at || (i === at && (s === "approved" || s === "live"));
        const current = i === at;
        return (
          <li key={s} aria-current={current ? "step" : undefined} className="grid gap-1">
            <span className={cn("block h-1.5 rounded-full", done ? "bg-emerald-500" : current ? (status === "changes_requested" ? "bg-amber-500" : "bg-primary") : "bg-muted")} />
            <span className={cn("hidden truncate text-[11px] sm:block", current ? "font-semibold text-foreground" : "text-muted-foreground")}>
              {done && !current ? <CheckCircle2 className="mr-0.5 inline h-3 w-3 text-emerald-500" aria-hidden /> : null}
              {t(`content.stage.${s}` as MessageKey)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function HistoryList({ orgId, id, locale }: { orgId: string; id: string; locale: "en" | "bn" }) {
  const t = useT();
  const { api } = useBrandex();
  const events = useQuery<Event[]>({
    queryKey: ["orgs", orgId, "deliverables", id, "events"],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/deliverables/{deliverable_id}/events", { params: { path: { org_id: orgId, deliverable_id: id } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  if (events.isPending) return <Spinner label={t("common.loading")} />;
  if (events.isError) return <Alert tone="error">{errorMessage(events.error, t)}</Alert>;
  return (
    <ol className="grid gap-1.5 border-l-2 border-border pl-4 text-sm">
      {events.data.map((e, i) => (
        <li key={i}>
          <span className="font-medium">{t(`content.status.${e.to}` as MessageKey)}</span>
          <span className="text-muted-foreground"> · {t(`content.by.${e.actor_kind}` as MessageKey)} · {formatRelative(locale, e.at)}</span>
          {e.comment ? <p className="text-muted-foreground">“{e.comment}”</p> : null}
        </li>
      ))}
    </ol>
  );
}

/** "Doctors Diary · Facebook", "Your Instagram" or "Your website". */
export function channelName(t: (k: MessageKey) => string, p: Pick<Publication, "platform" | "account">): string {
  const platform = t(`content.platform.${p.platform}` as MessageKey);
  if (p.platform === "website") return platform;
  return p.account === "doctors_diary" ? `${t("content.doctorsDiary")} · ${platform}` : `${t("content.yours")} ${platform}`;
}
